import { useState, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../../../../features/auth/store'
import { merchantsApi } from '../../../../features/merchants/api/merchantsApi'
import type { Order } from '../../types/orders.types'
import { Modal } from '../../../../shared/components/Modal'
import { useToast } from '../../../../shared/components/toast'
import { useCurrencyFormatter } from '../../../../shared/utils/currencyUtils'
import { formatOrderDate } from '../../../../shared/utils/utils'
import { realOrdersApi } from '../../api/ordersApiReal'
import { handleApiError } from '../../../../shared/utils/handleApiError'
import { getAllowedStatusTransitions, getStatusLabel } from '../../utils/orderStatusUtils'
import { FulfillmentCard } from './FulfillmentCard'
import { StatusHistoryCard } from './StatusHistoryCard'
import type { UserRole } from '../../../auth/type'

interface OrderDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  order: Order | null
  onOrderUpdate?: () => void
  /** User's role for permission-based status updates */
  userRole?: UserRole
}

export const OrderDetailsModal = ({ isOpen, onClose, order, onOrderUpdate, userRole }: OrderDetailsModalProps) => {
  const { toast } = useToast()
  const { formatPrice } = useCurrencyFormatter()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<Order['status']>(order?.status || 'PROCESSING')
  const [notes, setNotes] = useState('')
  const [showDispatchForm, setShowDispatchForm] = useState(false)
  const [dispatchCarrier, setDispatchCarrier] = useState('')
  const [dispatchTracking, setDispatchTracking] = useState('')
  const [isDispatching, setIsDispatching] = useState(false)
  const carrierInputRef = useRef<HTMLInputElement>(null)

  const { user } = useAuthStore()
  const isCollectiveStaff = user?.roleScope === 'COLLECTIVE'
  const { data: merchantData } = useQuery({
    queryKey: ['merchants', order?.merchantId],
    queryFn: () => merchantsApi.get(order!.merchantId),
    enabled: !!(isCollectiveStaff && order?.merchantId),
    staleTime: 2 * 60 * 1000,
  })

  // The LIST read omits `history` and returns thinner fulfilment data than the by-id read, so fetch
  // the detail once the modal opens and prefer it. Falls back to the list row while it loads (and if
  // it fails), so the modal never blanks out on a fetch error.
  const { data: detailedOrder } = useQuery({
    queryKey: ['order', order?.id],
    queryFn: () => realOrdersApi.getOrder(order!.id),
    enabled: !!(isOpen && order?.id),
    staleTime: 30 * 1000,
  })
  const fullOrder = detailedOrder ?? order

  // Update status when order changes
  useEffect(() => {
    if (order?.status) {
      setStatus(order.status)
    }
  }, [order?.status])

  if (!order) return null

  const TERMINAL_ORDER_STATUSES: Order['status'][] = [
    'DELIVERED', 'CANCELLED', 'RETURNED', 'REFUNDED', 'TRANSFER_FAILED',
  ]

  const hasExistingShipment = !!(order?.dhlTrackingNumber || order?.manualTrackingNumber)
  const canDispatchManually = !hasExistingShipment && !TERMINAL_ORDER_STATUSES.includes(status)

  const getStatusColor = (s: Order['status']) => {
    switch (s) {
      case 'PROCESSING':
        return '#E2DEF7'
      case 'EN_ROUTE':
        return '#FEFECD'
      case 'DELIVERED':
        return '#E1FEE3'
      case 'CANCELLED':
        return '#FECDCD'
      case 'REFUNDED':
        return '#FECDCD'
      case 'TRANSFER_FAILED':
        return '#FECDCD'
      case 'RETURNED':
        return '#FEE4CD'
      default:
        return '#E2DEF7'
    }
  }

  const handleManualDispatch = async () => {
    if (!order || !dispatchCarrier.trim() || !dispatchTracking.trim()) return
    setIsDispatching(true)
    try {
      await realOrdersApi.markManualShipment(order.id, {
        carrier: dispatchCarrier.trim(),
        trackingNumber: dispatchTracking.trim(),
      })
      // Backend sets status to EN_ROUTE
      setStatus('EN_ROUTE')
      setShowDispatchForm(false)
      setDispatchCarrier('')
      setDispatchTracking('')
      toast.success('Order dispatched', `Order #${order.orderId} marked as dispatched via ${dispatchCarrier.trim()}`)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      if (onOrderUpdate) {
        onOrderUpdate()
      }
    } catch (error) {
      console.error('[OrderDetailsModal] Error marking manual shipment:', error)
      const errorMessage = handleApiError(error)
      toast.error('Failed to mark as dispatched', errorMessage)
    } finally {
      setIsDispatching(false)
    }
  }

  const handleStatusUpdate = async (newStatus: Order['status']) => {
    if (!order) return
    
    try {
      await realOrdersApi.updateOrderStatus(order.id, newStatus)
      setStatus(newStatus)
      toast.success('Order status updated', `Order #${order?.orderId} status changed to ${newStatus}`)
      
      // Invalidate and refetch orders queries
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      
      // Refresh the orders list
      if (onOrderUpdate) {
        onOrderUpdate()
      }
    } catch (error) {
      console.error('[OrderDetailsModal] Error updating status:', error)
      const errorMessage = handleApiError(error)
      toast.error('Failed to update order status', errorMessage)
    }
  }

  const handleNotesUpdate = () => {
    if (notes.trim()) {
      toast.success('Notes saved', 'Order notes have been updated')
      // TODO: Make API call to save notes when backend integration is ready
    } else {
      toast.info('Notes cleared', 'Order notes have been cleared')
    }
  }

  const handleDownloadInvoice = () => {
    toast.info('Downloading invoice...', `Preparing invoice for order #${order.id}`)
    // TODO: Implement actual invoice download when backend integration is ready
    // For now, simulate download with timeout
    setTimeout(() => {
      toast.success('Invoice downloaded', `Invoice for order #${order.id} has been downloaded`)
    }, 2000)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-[70dvw] max-w-[1400px] min-w-[1000px] h-[90vh] rounded-[10px] overflow-hidden">
      <div className="w-full h-full p-[48px] flex flex-col gap-[24px] overflow-y-auto rounded-[10px]">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-[12px]">
            <h2
              className="text-[23px] font-bold"
              style={{
                lineHeight: '140%',
                letterSpacing: '0%',
                background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Order Details
            </h2>
            {(order.friendlyOrderId ?? order.orderId) && (
              <span
                className="px-[10px] py-[4px] rounded-[6px] text-[13px] font-mono font-semibold"
                style={{ backgroundColor: '#F3F1F2', color: '#3F3E42' }}
                title="Order ID"
              >
                #{order.friendlyOrderId ?? order.orderId}
              </span>
            )}
            {order.orderType === 'BESPOKE' && (
              <span
                className="px-[10px] py-[4px] rounded-[6px] text-[13px] font-semibold"
                style={{ backgroundColor: '#E2DEF7', color: '#28282A' }}
              >
                Bespoke
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-[16px]">
            <button
              onClick={handleDownloadInvoice}
              className="w-[200px] h-[40px] rounded-[10px] border border-[#B8B5B6] flex items-center justify-center gap-[10px] p-[10px] hover:bg-muted transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 10.6667L5.33333 8H6.66667V4H9.33333V8H10.6667L8 10.6667Z" fill="#3F3E42"/>
                <path d="M12.6667 10V12.6667H3.33333V10H2V12.6667C2 13.4 2.6 14 3.33333 14H12.6667C13.4 14 14 13.4 14 12.6667V10H12.6667Z" fill="#3F3E42"/>
              </svg>
              <span 
                className="text-[14px] font-semibold"
                style={{
                  lineHeight: '140%',
                  letterSpacing: '0%',
                  background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                Download Invoice
              </span>
            </button>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-full transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 6L18 18" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-[60%_40%] gap-[24px] flex-1">
          {/* Left Column */}
          <div className="flex flex-col gap-[24px]">
            {/* Customer Information Card */}
            <div className="border border-[#B8B5B6] rounded-[25px] bg-card p-[20px]">
              <div className="flex items-center gap-[15px] mb-[20px]">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3 
                  className="text-[19px] font-semibold"
                  style={{
                    lineHeight: '140%',
                    letterSpacing: '0%',
                    background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  Customer Information
                </h3>
              </div>
              
              <div className={`grid ${isCollectiveStaff ? 'grid-cols-4' : 'grid-cols-3'} gap-[14px]`}>
                <div>
                  <p 
                    className="text-[14px] font-semibold mb-[8px]"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#7C797C'
                    }}
                  >
                    Customer Name
                  </p>
                  <p 
                    className="text-[16px] font-semibold break-words"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                    title={order.customer.name}
                  >
{order.customer.name}
                  </p>
                </div>
                
                <div>
                  <p 
                    className="text-[14px] font-semibold mb-[8px]"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#7C797C'
                    }}
                  >
                    Email Address
                  </p>
                  <div className="flex items-start gap-[4px]">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 mt-1">
                      <path d="M2.66667 2.66667H13.3333C14.0667 2.66667 14.6667 3.26667 14.6667 4V12C14.6667 12.7333 14.0667 13.3333 13.3333 13.3333H2.66667C1.93333 13.3333 1.33333 12.7333 1.33333 12V4C1.33333 3.26667 1.93333 2.66667 2.66667 2.66667Z" stroke="#3F3E42" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14.6667 4L8 8.66667L1.33333 4" stroke="#3F3E42" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p 
                      className="text-[16px] font-semibold break-words"
                      style={{
                        lineHeight: '140%',
                        letterSpacing: '0%',
                        background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word'
                      }}
                      title={order.customer.email}
                    >
{order.customer.email}
                    </p>
                  </div>
                </div>
                
                <div>
                  <p 
                    className="text-[14px] font-semibold mb-[8px]"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#7C797C'
                    }}
                  >
                    Phone Number
                  </p>
                  <div className="flex items-center gap-[4px]">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.6667 11.28V13.28C14.6675 13.4657 14.6294 13.6494 14.5548 13.8195C14.4801 13.9897 14.3708 14.1422 14.2347 14.2679C14.0987 14.3936 13.9393 14.4893 13.7668 14.5492C13.5943 14.6091 13.4124 14.6319 13.2333 14.6167C11.1693 14.3899 9.19337 13.6894 7.46 12.5733C5.84572 11.5617 4.50828 10.2242 3.49667 8.61C2.37667 6.86856 1.67619 4.88372 1.45333 2.81C1.43814 2.63187 1.46046 2.45077 1.51974 2.27909C1.57903 2.10741 1.6738 1.94873 1.79847 1.81346C1.92314 1.67819 2.0746 1.56939 2.24348 1.49472C2.41235 1.42005 2.59483 1.38114 2.78 1.38H4.78C5.09334 1.37682 5.39681 1.48308 5.6398 1.68478C5.88279 1.88648 6.04888 2.17125 6.10667 2.48C6.21334 3.096 6.38 3.69734 6.60667 4.276C6.70191 4.52146 6.72074 4.79065 6.66077 5.04894C6.6008 5.30723 6.46479 5.54361 6.27333 5.72667L5.39333 6.60667C6.34235 8.27571 7.72429 9.65765 9.39333 10.6067L10.2733 9.72667C10.4564 9.53521 10.6928 9.3992 10.9511 9.33923C11.2093 9.27926 11.4785 9.29809 11.724 9.39333C12.3027 9.62 12.904 9.78667 13.52 9.89333C13.8336 9.95209 14.1221 10.1212 14.3234 10.3699C14.5248 10.6186 14.6262 10.9274 14.6167 11.24L14.6667 11.28Z" stroke="#3F3E42" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p 
                      className="text-[16px] font-semibold"
                      style={{
                        lineHeight: '140%',
                        letterSpacing: '0%',
                        background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      {order.shipping.address.phone || order?.customer?.phone|| order?.contactPhone || order?.address?.phone || 'N/A'}
                    </p>
                  </div>
                </div>

                {isCollectiveStaff && (
                  <div>
                    <p 
                      className="text-[14px] font-semibold mb-[8px]"
                      style={{
                        lineHeight: '140%',
                        letterSpacing: '0%',
                        color: '#7C797C'
                      }}
                    >
                      Merchant
                    </p>
                    <p 
                      className="text-[16px] font-semibold break-words"
                      style={{
                        lineHeight: '140%',
                        letterSpacing: '0%',
                        background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                      title={merchantData?.name || 'Loading...'}
                    >
                      {merchantData?.name || '—'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Shipping Information Card */}
            <div className="border border-[#B8B5B6] rounded-[25px] bg-card p-[20px]">
              <div className="flex items-center gap-[15px] mb-[20px]">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V8L16 3Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3V8H21" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 18V12" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 15L12 12L15 15" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3 
                  className="text-[19px] font-semibold"
                  style={{
                    lineHeight: '140%',
                    letterSpacing: '0%',
                    background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  Shipping Information
                </h3>
              </div>
              
              <div className="grid grid-cols-3 gap-[14px]">
                <div>
                  <p 
                    className="text-[14px] font-semibold mb-[8px]"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#7C797C'
                    }}
                  >
                    Full Shipping Address
                  </p>
                  <p 
                    className="text-[16px] font-semibold"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                    title={`${order.shipping.address.street}, ${order.shipping.address.city}, ${order.shipping.address.country}`}
                  >
                    {order.shipping.address.street && (
                      <span className="block truncate">{order.shipping.address.street}</span>
                    )}
                    {order.shipping.address.city && (
                      <span className="block truncate">{order.shipping.address.city}, {order.shipping.address.country}</span>
                    )}
                  </p>
                </div>
                
                <div>
                  <p 
                    className="text-[14px] font-semibold mb-[8px]"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#7C797C'
                    }}
                  >
                    Order Date
                  </p>
                  <p 
                    className="text-[16px] font-semibold"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    {order?.date === "Not Specified" ? "Not Specified" : formatOrderDate(order.date)}
                  </p>
                </div>
                
                <div>
                  <p 
                    className="text-[14px] font-semibold mb-[8px]"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#7C797C'
                    }}
                  >
                    Estimated Delivery
                  </p>
                  <p 
                    className="text-[16px] font-semibold"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    {order.shipping.estimatedDelivery === "Not Specified" ? "Not Specified" : formatOrderDate(order.shipping.estimatedDelivery)}
                  </p>
                </div>
              </div>
            </div>

            {/* Fulfilment & tracking + status timeline (PE-2167) — both read from the by-id
                detail, which carries fields the list read does not return. */}
            {fullOrder && <FulfillmentCard order={fullOrder} />}
            {fullOrder && <StatusHistoryCard order={fullOrder} />}

            {/* Order Items / Bespoke Details Card */}
            {order.orderType === 'BESPOKE' && order.bespokeDetails ? (
              <div className="border border-[#B8B5B6] rounded-[25px] bg-card p-[20px] flex-1">
                <div className="flex items-center gap-[15px] mb-[20px]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 6h18" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 10a4 4 0 01-8 0" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3
                    className="text-[19px] font-semibold"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    Bespoke Details
                  </h3>
                </div>

                {(() => {
                  const bd = order.bespokeDetails!
                  const labelStyle = { color: '#7C797C', lineHeight: '140%', letterSpacing: '0%' }
                  const valueStyle = {
                    lineHeight: '140%',
                    letterSpacing: '0%',
                    background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                    WebkitBackgroundClip: 'text' as const,
                    WebkitTextFillColor: 'transparent' as const,
                    backgroundClip: 'text' as const,
                  }
                  const Field = ({ label, value }: { label: string; value: string }) => (
                    <div>
                      <p className="text-[14px] font-semibold mb-[8px]" style={labelStyle}>{label}</p>
                      <p className="text-[16px] font-semibold" style={valueStyle}>{value}</p>
                    </div>
                  )
                  return (
                    <>
                      <div className="grid grid-cols-3 gap-[14px] mb-[14px]">
                        <Field label="Garment Type" value={bd.garmentType} />
                        <Field label="Fabric Choice" value={bd.fabricChoice} />
                        <Field label="Size & Fit" value={bd.sizeAndFitPreference} />
                      </div>
                      <div className="grid grid-cols-3 gap-[14px] mb-[14px]">
                        <Field label="Colour" value={bd.color} />
                        <Field label="Quantity" value={String(bd.quantity)} />
                        <Field label="Delivery Type" value={bd.deliveryType === 'DELIVERY' ? 'Delivery' : 'Pickup'} />
                      </div>
                      <div className="grid grid-cols-3 gap-[14px] mb-[14px]">
                        <Field label="Selling Price" value={formatPrice(bd.sellingPrice)} />
                        <Field label="Cost Price" value={formatPrice(bd.costPrice)} />
                        {bd.deliveryType === 'DELIVERY' && (
                          <Field label="Delivery Fee" value={formatPrice(bd.deliveryFee)} />
                        )}
                        <Field label="Est. Delivery Date" value={bd.estimatedDeliveryDate ? formatOrderDate(bd.estimatedDeliveryDate) : 'N/A'} />
                      </div>

                      {bd.imageUrls && bd.imageUrls.length > 0 && (
                        <div className="mb-[14px]">
                          <p className="text-[14px] font-semibold mb-[8px]" style={labelStyle}>Reference Images</p>
                          <div className="flex gap-[8px] overflow-x-auto pb-[4px]">
                            {bd.imageUrls.map((url, i) => (
                              <img
                                key={i}
                                src={url}
                                alt={`Reference ${i + 1}`}
                                className="w-[80px] h-[80px] object-cover rounded-[8px] border border-[#E5E7EB] flex-shrink-0"
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="border-t border-[#E5E7EB] pt-[16px] mt-[16px]">
                        <div className="flex justify-between items-center">
                          <span className="text-[16px] font-bold" style={valueStyle}>Total Order</span>
                          <span className="text-[20px] font-bold" style={valueStyle}>
                            {formatPrice(bd.sellingPrice * bd.quantity)}
                          </span>
                        </div>
                      </div>
                    </>
                  )
                })()}
              </div>
            ) : (
              <div className="border border-[#B8B5B6] rounded-[25px] bg-card p-[20px] flex-1">
                <div className="flex items-center gap-[15px] mb-[20px]">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1666 12.005 21.1666C11.7422 21.1666 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L2 12V2H12L20.59 10.59C20.9625 10.9647 21.1716 11.4716 21.1716 12C21.1716 12.5284 20.9625 13.0353 20.59 13.41V13.41Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7 7H7.01" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <h3
                    className="text-[19px] font-semibold"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    Order Items ({order.items.count})
                  </h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-[14px] min-w-[800px]">
                    <thead>
                      <tr className="border-b border-[#E5E7EB]">
                        <th className="text-left pb-[8px] font-semibold w-[30%] min-w-[200px]" style={{ color: '#7C797C' }}>Product Name</th>
                        <th className="text-left pb-[8px] font-semibold w-[10%] min-w-[80px]" style={{ color: '#7C797C' }}>Color</th>
                        <th className="text-left pb-[8px] font-semibold w-[10%] min-w-[60px]" style={{ color: '#7C797C' }}>Size</th>
                        <th className="text-left pb-[8px] font-semibold w-[8%] min-w-[50px]" style={{ color: '#7C797C' }}>Qty</th>
                        <th className="text-left pb-[8px] font-semibold w-[12%] min-w-[80px]" style={{ color: '#7C797C' }}>Cost (₦)</th>
                        <th className="text-left pb-[8px] font-semibold w-[15%] min-w-[100px]" style={{ color: '#7C797C' }}>Unit Price</th>
                        <th className="text-right pb-[8px] font-semibold w-[15%] min-w-[100px]" style={{ color: '#7C797C' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {order.items.details.map((item) => {
                        const cellStyle = {
                          lineHeight: '140%',
                          letterSpacing: '0%',
                          background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                          WebkitBackgroundClip: 'text' as const,
                          WebkitTextFillColor: 'transparent' as const,
                          backgroundClip: 'text' as const,
                        }
                        return (
                          <tr key={item.id}>
                            <td className="py-[12px] font-semibold max-w-[200px]" style={cellStyle} title={item.name}>
                              <div className="truncate">{item.name}</div>
                            </td>
                            <td className="py-[12px] font-semibold" style={cellStyle}>{item.color}</td>
                            <td className="py-[12px] font-semibold" style={cellStyle}>{item.size}</td>
                            <td className="py-[12px] font-semibold" style={cellStyle}>{item.quantity}</td>
                            <td className="py-[12px] font-semibold" style={cellStyle}>{formatPrice(item.costPrice)}</td>
                            <td className="py-[12px] font-semibold" style={cellStyle}>{formatPrice(item.unitPrice)}</td>
                            <td className="py-[12px] text-right font-semibold" style={cellStyle}>{formatPrice(item.total)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>

                  <div className="border-t border-[#E5E7EB] pt-[16px] mt-[16px]">
                    <div className="flex justify-between items-center">
                      <span
                        className="text-[16px] font-bold"
                        style={{
                          lineHeight: '140%',
                          letterSpacing: '0%',
                          background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}
                      >
                        Total Order
                      </span>
                      <span
                        className="text-[20px] font-bold"
                        style={{
                          lineHeight: '140%',
                          letterSpacing: '0%',
                          background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}
                      >
                        {formatPrice(order.totalAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="flex flex-col gap-[24px]">
            {/* Order Status Management Card */}
            <div className="border border-[#B8B5B6] rounded-[25px] bg-card p-[20px]">
              <div className="flex items-center gap-[15px] mb-[20px]">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17H8" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 9H9H8" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3 
                  className="text-[19px] font-semibold"
                  style={{
                    lineHeight: '140%',
                    letterSpacing: '0%',
                    background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  Order Status Management
                </h3>
              </div>
              
              <div>
                <p 
                  className="text-[14px] font-semibold mb-[8px]"
                  style={{
                    lineHeight: '140%',
                    letterSpacing: '0%',
                    color: '#7C797C'
                  }}
                >
                  Current Status
                </p>
                <div 
                  className="px-[12px] py-[8px] rounded-[8px] font-medium mb-[16px] inline-block"
                  style={{
                    backgroundColor: getStatusColor(status),
                    color: '#28282A'
                  }}
                >
                  {getStatusLabel(status)}
                </div>
              </div>
              
              {/* RETURNED edge-state note */}
              {status === 'RETURNED' && (
                <p
                  className="text-[13px] font-medium mb-[8px] px-[12px] py-[8px] rounded-[8px]"
                  style={{ backgroundColor: '#FEE4CD', color: '#28282A' }}
                >
                  This order was returned to sender.
                </p>
              )}

              {/* Only show status update dropdown if user can update to at least one status */}
              {getAllowedStatusTransitions(status, userRole).length > 0 && (
                <div>
                  <p
                    className="text-[14px] font-semibold mb-[8px]"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#7C797C'
                    }}
                  >
                    Update Status
                  </p>
                  <div className="relative inline-block">
                    <select
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          handleStatusUpdate(e.target.value as Order['status'])
                        }
                      }}
                      className="px-[12px] py-[8px] rounded-[8px] font-medium appearance-none cursor-pointer pr-[32px] border border-[#B8B5B6]"
                      style={{
                        lineHeight: '140%',
                        letterSpacing: '0%',
                        color: '#28282A'
                      }}
                    >
                      <option value="">Select new status...</option>
                      {getAllowedStatusTransitions(status, userRole).map((targetStatus) => (
                        <option key={targetStatus} value={targetStatus}>
                          {getStatusLabel(targetStatus)}
                        </option>
                      ))}
                    </select>
                    <svg className="absolute right-[12px] top-[12px] pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 6L8 10L12 6" stroke="#3F3E42" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              )}

              {/* Mark as dispatched — manual (non-DHL) couriers */}
              {canDispatchManually && (
                <div className="mt-[16px] border-t border-[#E5E7EB] pt-[16px]">
                  {!showDispatchForm ? (
                    <button
                      onClick={() => {
                        setShowDispatchForm(true)
                        setTimeout(() => carrierInputRef.current?.focus(), 0)
                      }}
                      className="w-full py-[10px] border border-[#3F3E42] rounded-[8px] hover:bg-[#3F3E42] hover:text-white transition-colors font-semibold text-[14px] text-foreground"
                    >
                      Mark as dispatched
                    </button>
                  ) : (
                    <div className="flex flex-col gap-[10px]">
                      <p
                        className="text-[14px] font-semibold"
                        style={{ color: '#7C797C' }}
                      >
                        Dispatch via courier
                      </p>
                      <input
                        ref={carrierInputRef}
                        type="text"
                        placeholder="Carrier (e.g. GIG Logistics)"
                        value={dispatchCarrier}
                        onChange={(e) => setDispatchCarrier(e.target.value)}
                        className="w-full px-[12px] py-[8px] border border-[#E5E7EB] rounded-[8px] text-[14px] text-foreground placeholder-[#7C797C] outline-none focus:border-[#3F3E42] transition-colors"
                      />
                      <input
                        type="text"
                        placeholder="Tracking number"
                        value={dispatchTracking}
                        onChange={(e) => setDispatchTracking(e.target.value)}
                        className="w-full px-[12px] py-[8px] border border-[#E5E7EB] rounded-[8px] text-[14px] text-foreground placeholder-[#7C797C] outline-none focus:border-[#3F3E42] transition-colors"
                      />
                      <div className="flex gap-[8px]">
                        <button
                          onClick={handleManualDispatch}
                          disabled={isDispatching || !dispatchCarrier.trim() || !dispatchTracking.trim()}
                          className="flex-1 py-[8px] bg-[#3F3E42] text-white rounded-[8px] font-semibold text-[14px] hover:bg-[#28282A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isDispatching ? 'Saving…' : 'Confirm dispatch'}
                        </button>
                        <button
                          onClick={() => {
                            setShowDispatchForm(false)
                            setDispatchCarrier('')
                            setDispatchTracking('')
                          }}
                          className="px-[16px] py-[8px] border border-[#B8B5B6] rounded-[8px] font-semibold text-[14px] text-foreground hover:bg-muted transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Payment Information Card */}
            <div className="border border-[#B8B5B6] rounded-[25px] bg-card p-[20px]">
              <div className="flex items-center gap-[15px] mb-[20px]">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" stroke="#6B7280" strokeWidth="2"/>
                  <line x1="1" y1="10" x2="23" y2="10" stroke="#6B7280" strokeWidth="2"/>
                </svg>
                <h3 
                  className="text-[19px] font-semibold"
                  style={{
                    lineHeight: '140%',
                    letterSpacing: '0%',
                    background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  Payment Information
                </h3>
              </div>

              <div className="space-y-[16px] text-[14px]">
                <div className="flex justify-between items-center">
                  <span 
                    className="font-semibold"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#7C797C'
                    }}
                  >
                    Payment Status:
                  </span>
                  <span 
                    className="px-[12px] py-[4px] rounded-[6px] font-medium"
                    style={{
                      backgroundColor: getStatusColor(status),
                      color: '#28282A'
                    }}
                  >
                    {order.payment}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span 
                    className="font-semibold"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#7C797C'
                    }}
                  >
                    Total Amount:
                  </span>
                  <span 
                    className="font-semibold"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    {formatPrice(order.totalAmount)}
                  </span>
                </div>
                
                {order.paymentDetails.method !== 'N/A' && (
                  <div className="flex justify-between items-center">
                    <span 
                      className="font-semibold"
                      style={{
                        lineHeight: '140%',
                        letterSpacing: '0%',
                        color: '#7C797C'
                      }}
                    >
                      Payment Credit Method
                    </span>
                    <span 
                      className="font-semibold"
                      style={{
                        lineHeight: '140%',
                        letterSpacing: '0%',
                        background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
  {order.paymentDetails.method}
                    </span>
                  </div>
                )}
                
                <hr className="border-[#E5E7EB]" />
                
                {order.paymentDetails.processingFee > 0 && (
                  <div className="flex justify-between items-center">
                    <span 
                      className="font-semibold"
                      style={{
                        lineHeight: '140%',
                        letterSpacing: '0%',
                        color: '#7C797C'
                      }}
                    >
                      Processing fee:
                    </span>
                    <span 
                      className="font-semibold"
                      style={{
                        lineHeight: '140%',
                        letterSpacing: '0%',
                        background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}
                    >
                      {formatPrice(order.paymentDetails.processingFee)}
                    </span>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span 
                    className="font-semibold"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      color: '#7C797C'
                    }}
                  >
                    Net Amount:
                  </span>
                  <span 
                    className="font-semibold"
                    style={{
                      lineHeight: '140%',
                      letterSpacing: '0%',
                      background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    {formatPrice(order.paymentDetails.netAmount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Internal Notes Card */}
            <div className="border border-[#B8B5B6] rounded-[25px] bg-card p-[20px]">
              <div className="flex items-center gap-[15px] mb-[20px]">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 3V7C14 7.26522 14.1054 7.51957 14.2929 7.70711C14.4804 7.89464 14.7348 8 15 8H19" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 21H7C6.46957 21 5.96086 20.7893 5.58579 20.4142C5.21071 20.0391 5 19.5304 5 19V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H14L19 8V19C19 19.5304 18.7893 20.0391 18.4142 20.4142C18.0391 20.7893 17.5304 21 17 21Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3 
                  className="text-[19px] font-semibold"
                  style={{
                    lineHeight: '140%',
                    letterSpacing: '0%',
                    background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  Internal Notes
                </h3>
              </div>

              <div className="flex flex-col gap-[16px]">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add internal notes about this order"
                  className="w-full flex-1 min-h-[120px] p-[12px] border border-[#E5E7EB] rounded-[8px] text-[14px] text-foreground placeholder-[#7C797C] resize-none outline-none focus:border-[#3F3E42] transition-colors"
                  style={{
                    lineHeight: '140%',
                    letterSpacing: '0%'
                  }}
                />
                
                <button
                  onClick={handleNotesUpdate}
                  className="w-full py-[12px] border border-[#3F3E42] rounded-[8px] hover:bg-[#3F3E42] hover:text-white transition-colors font-semibold text-foreground"
                >
                  Save Notes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}